// @ts-nocheck

import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import useRouter from '../../hooks/useRouter';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { ZIndices } from '../../styled-components/dark/common';
import { KeyCodes } from '../../types/keyboard';
import type { SearchResultsType } from '../../types/search';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import SearchInput from '../SearchInput';
import SearchResults from '../SearchResults';

const RESULT_WRAPPER_BORDER_RADIUS = 15; // px

const Wrapper = styled.div``;

const Suggestion = styled.div`
  box-sizing: border-box;
  padding: 0.625em;
  color: ${theme.SearchSuggestion.color};
  background-color: ${theme.SearchSuggestion.backgroundColor};
  ${({ $highlighted }) =>
    $highlighted
      ? css`
          background-color: ${theme.SearchSuggestion.highlightBackgroundColor};
        `
      : null};
  cursor: pointer;
`;

const SuggestionTitle = styled.div`
  text-align: left;
  align-items: center;
  display: flex;
`;

const Bold = styled.div`
  font-style: italic;
  font-weight: bold;
`;

const SearchResultsAnchor = styled.div`
  position: relative;
  margin-top: ${!Target.__MOBILE__ ? '1.4em' : undefined};
`;

const WRAPPER_PADDING = 0.8;

const SearchResultsWrapper = styled.div`
  ${(p) => {
    if (!Target.__MOBILE__) {
      return css`
        position: absolute;
        top: 0;
        width: calc(130% - ${2 * WRAPPER_PADDING}em);
        @media screen and (min-width: ${p.theme.breakpoints.md}px) {
          width: calc(140% - ${2 * WRAPPER_PADDING}em);
        }
        min-width: 400px;
        border-radius: ${RESULT_WRAPPER_BORDER_RADIUS}px;
        overflow: hidden;
        padding: ${WRAPPER_PADDING}em;

        background-color: rgba(0, 0, 0, 0.95);
        z-index: ${ZIndices.searchResultsDesktop};
        box-shadow:
          0 4px 5px 0 rgba(0, 0, 0, 0.14),
          0 1px 10px 0 rgba(0, 0, 0, 0.12),
          0 2px 4px -1px rgba(0, 0, 0, 0.3);
      `;
    }
  }};
`;

type PropsType = {
  focusOnMount?: boolean;
  suggestion: string;
  results: SearchResultsType;
  query: string;
  isLoading: boolean;
  clearSearch: () => void;
  search: (suggestion: string) => void;
  className?: string;
};

const SearchBar = ({
  focusOnMount,
  suggestion,
  results,
  query,
  clearSearch,
  isLoading,
  search,
  className,
}: PropsType) => {
  const { history } = useRouter();

  const inputRef = useRef();
  const [index, setIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);

  const highlightSuggestion = suggestion && index === 0;
  const highlightResultsIndex = Math.max(suggestion ? index - 1 : index, -1);
  const resultsLength = useMemo(
    () => Object.keys(results).reduce((acc, key) => acc + results[key].length, 0),
    [results],
  );

  const onBlur = useCallback(() => {
    setIsFocused(false);
    const inputNode = inputRef.current;
    if (inputNode) {
      inputNode.blur();
    }
  }, []);

  const acceptSuggestion = useCallback(() => {
    search(suggestion);
  }, [search, suggestion]);

  const focusInput = useCallback(() => {
    const inputNode = inputRef.current;
    if (inputNode) {
      inputNode.focus();
    }
  }, []);

  const onUpArrow = useCallback(() => {
    if (isFocused && (resultsLength || suggestion)) {
      setIndex((prev) => (prev > -1 ? prev - 1 : prev));
    }
  }, [isFocused, resultsLength, suggestion]);

  const onDownArrow = useCallback(() => {
    if (isFocused && (resultsLength || suggestion)) {
      const lastIndex = resultsLength + (suggestion ? 1 : 0) - 1;
      setIndex((prev) => (prev < lastIndex ? prev + 1 : prev));
    }
  }, [isFocused, resultsLength, suggestion]);

  const triggerSelection = useCallback(() => {
    const { matches = [], tournaments = [], bets = [], outcomes = [] } = results;

    if (highlightSuggestion) {
      acceptSuggestion();
    } else if (results && highlightResultsIndex > -1) {
      clearSearch();
      let to = null;

      const nbOutcomes = outcomes?.length ?? 0;
      const nbMatches = matches?.length ?? 0;
      const nbTournaments = tournaments?.length ?? 0;

      if (highlightResultsIndex < nbTournaments) {
        const index = highlightResultsIndex;
        const tournament = tournaments[index];
        to = RoutesRedirectionFactory.getCatalogRoutePathname(
          tournament.sportId,
          tournament.categoryId,
          tournament.tournamentId,
        );
      } else if (highlightResultsIndex < nbTournaments + nbMatches) {
        const index = highlightResultsIndex - nbTournaments;
        const match = matches[index];
        const matchRoute = RoutesRedirectionFactory.getMatchRoutePathname(match.matchId);

        if (matchRoute) {
          to = matchRoute;
        }
      } else if (highlightResultsIndex < nbTournaments + nbMatches + nbOutcomes) {
        const index = highlightResultsIndex - nbTournaments - nbMatches;
        const { matchId, outcomeId } = outcomes[index];
        const matchRoute = RoutesRedirectionFactory.getMatchRoutePathname(matchId);

        if (matchRoute) {
          to = `${matchRoute}#o=${outcomeId}`;
        }
      } else {
        const index = highlightResultsIndex - nbMatches - nbTournaments - nbOutcomes;
        const { matchId, betId } = bets[index];
        const matchRoute = RoutesRedirectionFactory.getMatchRoutePathname(matchId);

        if (matchRoute) {
          to = `${matchRoute}#b=${betId}`;
        }
      }

      if (to) {
        history.push(to);
      }
    }
  }, [results, history, highlightSuggestion, highlightResultsIndex, acceptSuggestion, clearSearch]);

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (isFocused) {
        if (e.keyCode === KeyCodes.ESCAPE || e.keyCode === KeyCodes.TAB) {
          e.preventDefault();
          onBlur();
        }
        if (e.keyCode === KeyCodes.UP_ARROW) {
          e.preventDefault();
          onUpArrow();
        }
        if (e.keyCode === KeyCodes.DOWN_ARROW) {
          e.preventDefault();
          onDownArrow();
        }
        if (e.keyCode === KeyCodes.ENTER) {
          e.preventDefault();
          triggerSelection();
        }
      }
    },
    [isFocused, onBlur, onUpArrow, onDownArrow, triggerSelection],
  );

  const handleChange = useCallback(
    (val: string) => {
      if (val?.length > 0) {
        search(val);
        setIndex(-1);
      } else {
        clearSearch();
      }
    },
    [search, clearSearch],
  );

  useEffect(() => {
    if (focusOnMount) {
      focusInput();
    }

    if (!Target.__MOBILE__) {
      document.addEventListener('keydown', handleKeydown);
    }

    return () => {
      if (!Target.__MOBILE__) {
        document.removeEventListener('keydown', handleKeydown);
      }
    };
  }, [focusInput, focusOnMount, handleKeydown, onBlur]);

  useEffect(() => {
    if (!Target.__MOBILE__ && document.body) {
      document.body.addEventListener('click', onBlur);
    }
    return () => {
      if (!Target.__MOBILE__ && document.body) {
        document.body.removeEventListener('click', onBlur);
      }
    };
  }, [onBlur]);

  const onSearchFocus = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    setIsFocused(true);
  }, []);

  return (
    <Wrapper className={className} onClick={onSearchFocus}>
      <SearchInput ref={inputRef} placeholder={__('Search')} value={query} onChange={handleChange} />

      {isFocused && query.length > 1 ? (
        <SearchResultsAnchor>
          <SearchResultsWrapper>
            {suggestion ? (
              <Suggestion onClick={acceptSuggestion} $highlighted={highlightSuggestion}>
                <SuggestionTitle>
                  {__('Try:')}
                  &nbsp;
                  <Bold>{suggestion}</Bold>
                </SuggestionTitle>
              </Suggestion>
            ) : null}
            {query.length > 1 ? (
              <SearchResults
                clearInput={clearSearch}
                isLoading={isLoading}
                results={results}
                highlightIndex={highlightResultsIndex}
              />
            ) : null}
          </SearchResultsWrapper>
        </SearchResultsAnchor>
      ) : null}
    </Wrapper>
  );
};

export default SearchBar;
